import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/cn";
import type { ChatMessage, Conversation, ConversationType } from "@/lib/chat-types";
import type { FundComplianceFileRef } from "@/lib/fund-compliance/api";
import {
  createFundComplianceChat,
  fetchFundComplianceChatMessages,
  fetchFundComplianceChats,
  fetchFundComplianceSkills,
  FundComplianceAuthError,
  uploadFundComplianceFile,
} from "@/lib/fund-compliance/api";
import { notifyFundComplianceAuthInvalidated } from "@/lib/fund-compliance/auth-events";
import { normalizeHistoryMessages } from "@/lib/fund-compliance/messages";
import { useChatSocket } from "@/hooks/useChatSocket";
import { useChatState } from "@/hooks/useChatState";
import { createUploadingAttachment, useUploadFlow } from "@/hooks/useUploadFlow";
import { ChatSidebar } from "@/components/chat/ChatSidebar";
import { ChatContainer } from "@/components/chat/ChatContainer";

const DEFAULT_REVIEW_PROMPT = "Please review this factsheet for MAS advertising compliance.";
const EMPTY_MESSAGES: ChatMessage[] = [];

function getConversationType(title: string): ConversationType {
  return /review|factsheet|report/i.test(title) ? "review" : "qa";
}

export function ChatLayout() {
  const navigate = useNavigate();
  const [state, dispatch] = useChatState();
  const { conversations, activeId, messagesByChat, cursorsByChat, draftAttachmentsByChat, error, queuedMessage, selectedSkillId } = state;

  /* ── Derived state ─────────────────────────────────────────────── */

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [activeId, conversations]
  );
  const activeMessages = activeId ? messagesByChat[activeId] ?? EMPTY_MESSAGES : EMPTY_MESSAGES;
  const pendingAttachments = activeId ? draftAttachmentsByChat[activeId] : undefined;
  const activeCursor = activeId ? cursorsByChat[activeId] : undefined;
  const unauthorizedHandledRef = useRef(false);

  /* ── Auth ───────────────────────────────────────────────────────── */

  const handleUnauthorized = useCallback(() => {
    if (unauthorizedHandledRef.current) return;
    unauthorizedHandledRef.current = true;
    notifyFundComplianceAuthInvalidated();
    navigate("/demo/fund-compliance-agent", { replace: true });
  }, [navigate]);

  /* ── Create conversation ───────────────────────────────────────── */

  const createConversation = useCallback(
    async (type: ConversationType, title: string): Promise<Conversation> => {
      const created = await createFundComplianceChat(selectedSkillId, title);
      const conversation: Conversation = {
        id: created.chatId,
        title,
        type,
        messageCount: 0,
        createdAt: Date.now(),
        skillId: created.skillId,
      };
      dispatch({ type: "CONVERSATION_CREATED", conversation });
      return conversation;
    },
    [dispatch, selectedSkillId]
  );

  /* ── Upload flow (active chat only) ────────────────────────────── */

  const handleAttachToActive = useUploadFlow({
    dispatch,
    activeId,
    onUnauthorized: handleUnauthorized,
  });

  /* ── Empty-state draft attachments (no active chat yet) ────────── */

  const [emptyDraftAttachments, setEmptyDraftAttachments] = useState<FundComplianceFileRef[]>([]);

  const handleAttachFile = useCallback(
    async (file: File) => {
      if (activeId) {
        await handleAttachToActive(file);
        return;
      }

      // No active chat — upload to local draft; chat is created on send
      dispatch({ type: "CLEAR_ERROR" });
      const uploading = createUploadingAttachment(file);
      setEmptyDraftAttachments((prev) => [...prev, uploading]);

      try {
        const uploaded = await uploadFundComplianceFile(file);
        setEmptyDraftAttachments((prev) =>
          prev.map((a) => (a.storageId === uploading.storageId ? uploaded : a))
        );
      } catch (err) {
        setEmptyDraftAttachments((prev) => prev.filter((a) => a.storageId !== uploading.storageId));
        if (err instanceof FundComplianceAuthError) {
          handleUnauthorized();
          return;
        }
        dispatch({ type: "BOOTSTRAP_ERROR", error: err instanceof Error ? err.message : "Unable to attach file." });
      }
    },
    [activeId, handleAttachToActive, dispatch, handleUnauthorized]
  );

  /* ── UI state ──────────────────────────────────────────────────── */

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ── Bootstrap: load skills + chats ────────────────────────────── */

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const [skills, chats] = await Promise.all([
          fetchFundComplianceSkills(),
          fetchFundComplianceChats(),
        ]);
        if (cancelled) return;

        const resolvedSkillId = skills[0]?.id ?? "compliance-qa";
        const mapped: Conversation[] = chats.map((chat) => ({
          id: chat.id,
          title: chat.title,
          type: getConversationType(chat.title),
          messageCount: 0,
          createdAt: chat.createdAt ?? Date.now(),
          skillId: chat.skillId ?? resolvedSkillId,
        }));

        dispatch({ type: "BOOTSTRAP_SUCCESS", conversations: mapped, skillId: resolvedSkillId });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof FundComplianceAuthError) {
          handleUnauthorized();
          return;
        }
        dispatch({ type: "BOOTSTRAP_ERROR", error: err instanceof Error ? err.message : "Unable to load conversations." });
      }
    }

    void bootstrap();
    return () => { cancelled = true; };
  }, [dispatch, handleUnauthorized]);

  /* ── WebSocket ─────────────────────────────────────────────────── */

  const handleHistoryLoaded = useCallback(
    (hasMore: boolean, nextBefore: string | null) => {
      if (!activeId) return;
      dispatch({ type: "WS_HISTORY_LOADED", chatId: activeId, hasMore, nextBefore });
    },
    [activeId, dispatch]
  );

  const handleQueueFlushed = useCallback(() => dispatch({ type: "FLUSH_QUEUE" }), [dispatch]);

  const { messages, status, send, prependMessages, toolUse } = useChatSocket({
    chatId: activeId,
    skillId: activeConversation?.skillId ?? selectedSkillId,
    initialMessages: activeMessages,
    queuedMessage: queuedMessage?.chatId === activeId ? queuedMessage : null,
    onQueueFlushed: handleQueueFlushed,
    onUnauthorized: handleUnauthorized,
    onHistoryLoaded: handleHistoryLoaded,
  });

  /* ── Track active chat id via ref for stale-guard in async handlers ─ */

  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  /* ── Load more history (scroll-up pagination, REST) ────────────── */

  const handleLoadMore = useCallback(async () => {
    if (!activeId) return;
    const cursor = cursorsByChat[activeId];
    if (!cursor || !cursor.hasMore || cursor.loading) return;

    // Use nextBefore cursor if provided; otherwise fall back to oldest message id
    const existingMessages = messagesByChat[activeId] ?? [];
    const before = cursor.nextBefore ?? existingMessages[0]?.id;
    if (!before) return;

    const chatId = activeId;
    dispatch({ type: "LOAD_MORE_START", chatId });

    try {
      const page = await fetchFundComplianceChatMessages(chatId, { before });
      const normalized = normalizeHistoryMessages(page.messages);

      // Update reducer cache (always, so switching away and back retains data)
      dispatch({
        type: "LOAD_MORE_SUCCESS",
        chatId,
        messages: normalized,
        hasMore: page.hasMore,
        nextBefore: page.nextBefore,
      });

      // Update socket state (UI source of truth) only if still on this chat
      if (activeIdRef.current === chatId) {
        prependMessages(normalized);
      }
    } catch (err) {
      dispatch({ type: "LOAD_MORE_ERROR", chatId });
      if (err instanceof FundComplianceAuthError) {
        handleUnauthorized();
        return;
      }
      dispatch({ type: "BOOTSTRAP_ERROR", error: err instanceof Error ? err.message : "Unable to load more messages." });
    }
  }, [activeId, cursorsByChat, messagesByChat, dispatch, handleUnauthorized, prependMessages]);

  /* ── Sync socket messages back to state ────────────────────────── */

  useEffect(() => {
    if (activeId) {
      dispatch({ type: "MESSAGES_UPDATED", chatId: activeId, messages });
    }
  }, [activeId, dispatch, messages]);

  /* ── Auto-prefetch one page when WS history indicates hasMore ──── */

  const autoPrefetchedChatsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!activeId) return;
    if (autoPrefetchedChatsRef.current.has(activeId)) return;
    const cursor = cursorsByChat[activeId];
    if (!cursor) return; // WS history not loaded yet
    if (!cursor.hasMore || cursor.loading) return;

    autoPrefetchedChatsRef.current.add(activeId);
    void handleLoadMore();
  }, [activeId, cursorsByChat, handleLoadMore]);

  /* ── Handlers ──────────────────────────────────────────────────── */

  const handleNewChat = useCallback(() => {
    dispatch({ type: "SELECT_CHAT", chatId: null });
    dispatch({ type: "CLEAR_ERROR" });
    setEmptyDraftAttachments([]);
    setSidebarOpen(false);
  }, [dispatch]);

  const handleSelect = useCallback((id: string) => {
    dispatch({ type: "SELECT_CHAT", chatId: id });
    setSidebarOpen(false);
    setEmptyDraftAttachments([]);
  }, [dispatch, setSidebarOpen]);

  const handleSend = useCallback(
    async (payload: { content: string; mode: "qa" | "review"; attachments?: FundComplianceFileRef[] }) => {
      // Empty state: create chat first, then queue the message for WS onopen
      if (!activeId) {
        const attachments = emptyDraftAttachments;
        if (attachments.some((a) => a.isUploading)) return; // still uploading
        const hasAttachments = attachments.length > 0;
        const trimmed = payload.content.trim();
        const content = trimmed || (hasAttachments ? DEFAULT_REVIEW_PROMPT : "");
        if (!content) return;

        const mode: "qa" | "review" = hasAttachments ? "review" : "qa";
        const title = hasAttachments
          ? attachments[0]?.filename.replace(/\.pdf$/i, "") ?? "New chat"
          : trimmed.slice(0, 40) || "New chat";

        try {
          const conversation = await createConversation(mode, title);
          dispatch({
            type: "QUEUE_MESSAGE",
            chatId: conversation.id,
            payload: {
              content,
              mode,
              attachments: hasAttachments ? attachments : undefined,
            },
          });
          if (hasAttachments) {
            dispatch({ type: "UPDATE_CONVERSATION_TYPE", chatId: conversation.id, conversationType: "review" });
          }
          setEmptyDraftAttachments([]);
        } catch (err) {
          if (err instanceof FundComplianceAuthError) {
            handleUnauthorized();
            return;
          }
          dispatch({ type: "BOOTSTRAP_ERROR", error: err instanceof Error ? err.message : "Unable to create conversation." });
        }
        return;
      }

      // Active chat: normal send
      const attachments = payload.attachments ?? [];
      const finalPayload = {
        ...payload,
        content: payload.content.trim() || (attachments.length > 0 ? DEFAULT_REVIEW_PROMPT : payload.content),
      };
      send(finalPayload);
      dispatch({ type: "MESSAGE_SENT", chatId: activeId, mode: finalPayload.mode });
    },
    [activeId, emptyDraftAttachments, createConversation, dispatch, handleUnauthorized, send]
  );

  const handleClearAttachment = useCallback(
    (index: number) => {
      if (!activeId) {
        setEmptyDraftAttachments((prev) => prev.filter((_, i) => i !== index));
        return;
      }
      const attachments = draftAttachmentsByChat[activeId] ?? [];
      const attachment = attachments[index];
      if (attachment) {
        dispatch({ type: "REMOVE_DRAFT_ATTACHMENT", chatId: activeId, storageId: attachment.storageId });
      }
    },
    [activeId, dispatch, draftAttachmentsByChat]
  );

  /* ── Render ────────────────────────────────────────────────────── */

  const composerMode = activeConversation?.type ?? "qa";
  const placeholder = composerMode === "review"
    ? "Ask a follow-up about this review..."
    : "Ask about MAS compliance requirements...";

  return (
    <div className="flex flex-1 min-h-0">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          "shrink-0 flex flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)] transition-all duration-200",
          "fixed inset-y-0 left-0 z-50 pt-[var(--topnav-height)]",
          "md:static md:z-auto md:pt-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          sidebarCollapsed ? "w-[56px]" : "w-64"
        )}
      >
        <ChatSidebar
          conversations={conversations}
          activeId={activeId}
          collapsed={sidebarCollapsed}
          onSelect={handleSelect}
          onNewChat={() => { void handleNewChat(); }}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          onClose={() => setSidebarOpen(false)}
        />
      </aside>

      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        <div className="flex items-center h-10 px-3 border-b border-[var(--color-border)] md:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex items-center justify-center w-8 h-8 rounded text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] hover:bg-[var(--color-border)]/50 transition-colors cursor-pointer"
            aria-label="Open conversations"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <span className="ml-2 text-[10px] tracking-widest uppercase text-[var(--color-fg-tertiary)]" style={{ fontFamily: "var(--font-mono)" }}>
            Fund Compliance Agent
          </span>
        </div>

        {error && (
          <div className="px-4 py-2 text-xs text-red-400 border-b border-[var(--color-border)]">
            {error}
          </div>
        )}

        <ChatContainer
          key={activeId ?? "empty"}
          isEmptyState={activeId === null}
          messages={messages}
          status={activeId ? status : "connected"}
          placeholder={placeholder}
          onSend={(p) => { void handleSend(p); }}
          onAttachFile={(file) => { void handleAttachFile(file); }}
          onClearAttachment={handleClearAttachment}
          composerMode={composerMode}
          pendingAttachments={activeId ? pendingAttachments : emptyDraftAttachments}
          onLoadMore={() => { void handleLoadMore(); }}
          hasMoreHistory={activeCursor?.hasMore ?? false}
          loadingMore={activeCursor?.loading ?? false}
          toolUse={toolUse}
        />
      </div>
    </div>
  );
}
